import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Typography, Grid, TextField, Switch, FormControlLabel, Paper } from '@mui/material';
import clientService from '../../services/clientService';
import { Client } from '../../types';
import PageContainer from '../../components/PageContainer';

const ClientsEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate();
  const location = useLocation();
  const [client, setClient] = useState<Partial<Client>>({});
  const [loading, setLoading] = useState(false);
  const isNew = id === 'new';

  useEffect(() => {
    // Check if we're duplicating a client (has duplicateData in location state)
    if (isNew && location.state?.duplicateData) {
      setClient(location.state.duplicateData);
      setLoading(false);
    } else if (!isNew && id) {
      setLoading(true);
      clientService.getOne(Number(id)).then(data => {
        setClient(data);
        setLoading(false);
      }).catch((error) => {
        console.error('Failed to load client:', error);
        setLoading(false);
      });
    }
  }, [id, isNew]);

  const handleChange = (field: keyof Client, value: any) => {
    setClient((prev: Partial<Client>) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      let saved;
      if (isNew) {
        saved = await clientService.create(client);
      } else {
        saved = await clientService.update(Number(id), client);
      }
      navigate('/app/admin/clients');
    } catch (error) {
      console.error('Save failed', error);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <PageContainer>
      <Typography 
        variant="subtitle1" 
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        {isNew ? (location.state?.duplicateData ? 'Duplicate Client' : 'Create Client') : 'Edit Client'}
      </Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">Basic Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField 
              fullWidth 
              label="Agency Name" 
              value={client.agency_name || ''} 
              onChange={e => handleChange('agency_name', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="URL" value={client.url || ''} onChange={e => handleChange('url', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={client.active ?? true} onChange={e => handleChange('active', e.target.checked)} />}
              label="Active"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={client.vcrm_sync ?? false} onChange={e => handleChange('vcrm_sync', e.target.checked)} />}
              label="VCRM Sync"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={client.use_landing_pages ?? false} onChange={e => handleChange('use_landing_pages', e.target.checked)} />}
              label="Use Landing Pages"
            />
          </Grid>
          {/* Add more sections and fields similarly for all groups and fields */}
          {/* For example: */}
          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">Contact Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Contact Name" value={client.contact_name || ''} onChange={e => handleChange('contact_name', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Contact Email" value={client.contact_email || ''} onChange={e => handleChange('contact_email', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Contact Phone Number" value={client.contact_phone_number || ''} onChange={e => handleChange('contact_phone_number', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="WhatsApp Link" value={client.whats_app_link || ''} onChange={e => handleChange('whats_app_link', e.target.value)} />
          </Grid>
          {/* Sending Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">Sending Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email Host" value={client.email_host || ''} onChange={e => handleChange('email_host', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email Port" value={client.email_port || ''} onChange={e => handleChange('email_port', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email Username" value={client.email_username || ''} onChange={e => handleChange('email_username', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email Password" value={client.email_password || ''} onChange={e => handleChange('email_password', e.target.value)} />
          </Grid>
          {/* Business Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">Business Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Company Name" value={client.company_name || ''} onChange={e => handleChange('company_name', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Company Address" value={client.company_address || ''} onChange={e => handleChange('company_address', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Company Phone Number" value={client.company_phone_number || ''} onChange={e => handleChange('company_phone_number', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Company Email" value={client.company_email || ''} onChange={e => handleChange('company_email', e.target.value)} />
          </Grid>
          {/* Branding */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">Branding</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Website Name" value={client.website_name || ''} onChange={e => handleChange('website_name', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Website URL" value={client.website_url || ''} onChange={e => handleChange('website_url', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Logo URL" value={client.logo_url || ''} onChange={e => handleChange('logo_url', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Favicon URL" value={client.favicon_url || ''} onChange={e => handleChange('favicon_url', e.target.value)} />
          </Grid>
          {/* Social Media */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">Social Media</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Facebook URL" value={client.facebook_url || ''} onChange={e => handleChange('facebook_url', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Twitter URL" value={client.twitter_url || ''} onChange={e => handleChange('twitter_url', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Instagram URL" value={client.instagram_url || ''} onChange={e => handleChange('instagram_url', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="LinkedIn URL" value={client.linkedin_url || ''} onChange={e => handleChange('linkedin_url', e.target.value)} />
          </Grid>
          {/* CRM Integration */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">CRM Integration</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="VCRM URL" value={client.vcrm_url || ''} onChange={e => handleChange('vcrm_url', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="VCRM API Key" value={client.vcrm_api_key || ''} onChange={e => handleChange('vcrm_api_key', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="VCRM Secret Key" value={client.vcrm_secret_key || ''} onChange={e => handleChange('vcrm_secret_key', e.target.value)} />
          </Grid>
          {/* DNS Settings */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">DNS Settings</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Domain Name" value={client.domain_name || ''} onChange={e => handleChange('domain_name', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="DNS Provider" value={client.dns_provider || ''} onChange={e => handleChange('dns_provider', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="DNS Username" value={client.dns_username || ''} onChange={e => handleChange('dns_username', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="DNS Password" value={client.dns_password || ''} onChange={e => handleChange('dns_password', e.target.value)} />
          </Grid>
          {/* Database Settings */}
          <Grid item xs={12}>
            <Typography variant="subtitle1">Database Settings</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Database Host" value={client.db_host || ''} onChange={e => handleChange('db_host', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Database Name" value={client.db_name || ''} onChange={e => handleChange('db_name', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Database Username" value={client.db_username || ''} onChange={e => handleChange('db_username', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Database Password" value={client.db_password || ''} onChange={e => handleChange('db_password', e.target.value)} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSave}>Save</Button>
          <Button onClick={() => navigate('/app/admin/clients')} sx={{ ml: 2 }}>Cancel</Button>
        </Box>
      </Paper>
    </PageContainer>
  );
};

export default ClientsEdit; 